import React, { useState } from "react";
import Slider from "react-slick";
import { useIsDesktop } from "../../../hooks/useMediaQuery";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Link } from "gatsby";
import Markdown from "react-markdown";
import "../../home-page/home.css";

const DynamicAdvisoryBanner = ({ bannerData }) => {
  const isDesktop = useIsDesktop();
  const {
    left_arrow,
    right_arrow,
    banner_images: slider_data,
  } = bannerData || {};

  if (!slider_data?.length) return null;

  const PrevArrow = ({ onClick }) => (
    <div
      onClick={onClick}
      className="absolute left-[5%] lg:left-[1%] lg:top-[50%] top-[95%] transform -translate-y-[50%] z-20 cursor-pointer"
      style={{ transform: "translateY(-50%)" }}
    >
      <img
        src={left_arrow?.url}
        alt="Prev"
        className="h-[8vw] w-[8vw] xl:h-[5vh] xl:w-[5vh]"
        loading="lazy"
        decoding="async"
      />
    </div>
  );

  const NextArrow = ({ onClick }) => (
    <div
      onClick={onClick}
      className="absolute right-[75%] lg:right-[1%] lg:top-[50%] top-[95%] transform -translate-y-[50%] z-20 cursor-pointer"
      style={{ transform: "translateY(-50%)" }}
    >
      <img
        src={right_arrow?.url}
        alt="Next"
        className="h-[8vw] w-[8vw] xl:h-[5vh] xl:w-[5vh]"
        loading="lazy"
        decoding="async"
      />
    </div>
  );

  const settings = {
    dots: false,
    dotsClass: "slick-dots custom-dots",
    infinite: true,
    autoplay: true,
    speed: 1500,
    cssEase: "ease-in-out",
    autoplaySpeed: 8000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: slider_data?.length > 1,
    fade: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
  };

  return (
    <div id="home_slick" className="relative">
      <Slider {...settings}>
        {slider_data?.length > 0 &&
          slider_data.map((item, index) => (
            <div
              key={index}
              className={`${
                item?.href ? "cursor-pointer" : ""
              } relative h-full`}
            >
              {/* Placeholder during SSR/hydration - prevents wrong image load */}
              {isDesktop === null && (
                <div
                  className="w-full min-h-[80vh] relative bg-gray-200 animate-pulse"
                  aria-hidden="true"
                  style={{ minHeight: "80vh" }}
                />
              )}

              {/* Desktop Banner - only loads on desktop */}
              {isDesktop === true &&
                (item?.image ? (
                  <div className="w-full h-full relative">
                    <Link
                      to={
                        item?.link_redirection ? item?.link_redirection : null
                      }
                      target={
                        item?.same_or_different_tab
                          ? item?.same_or_different_tab
                          : undefined
                      }
                      rel={
                        item?.same_or_different_tab === "_blank"
                          ? "noopener noreferrer"
                          : undefined
                      }
                    >
                      <img
                        src={item?.image?.url}
                        alt={item?.image?.alternativeText || "Banner image"}
                        className="w-full h-full object-cover"
                        loading={index === 0 ? "eager" : "lazy"}
                        fetchPriority={index === 0 ? "high" : undefined}
                      />
                      <div
                        className={`drop-shadow-lg lg:w-[45%] absolute top-[6vw] right-[1.6vw] pr-[2vw] text-white`}
                      >
                        {item?.banner_text_1 && (
                          <h1 className="font-redhatMedium text-[2.5vw] leading-[2.75vw]">
                            {item?.banner_text_1}
                          </h1>
                        )}
                        {item?.heading && (
                          <h1 className="font-redhatBold text-[3.5vw] leading-[3.5vw] py-[1vw]">
                            {item?.heading}
                          </h1>
                        )}
                        {item?.description_1 && (
                          <div className="py-[0.5vw]">
                            {item?.description_1
                              ?.split("\n")
                              .map((line, idx) => (
                                <div
                                  key={idx}
                                  className="font-redhatRegular lg:py-[0.1vw] py-[0.5vw] pr-[1vw] text-[1.3vw] leading-tight"
                                >
                                  <Markdown
                                    components={{
                                      a: ({ href, children, ...props }) => (
                                        <a
                                          href={href}
                                          {...props}
                                          target="_blank"
                                        >
                                          {children}
                                        </a>
                                      ),
                                    }}
                                  >
                                    {line}
                                  </Markdown>
                                </div>
                              ))}
                          </div>
                        )}
                        {item?.description_2 && (
                          <div className="pt-[1vw]">
                            {item?.description_2
                              ?.split("\n")
                              .map((line, idx) => (
                                <div
                                  key={idx}
                                  className="font-redhatRegular text-[1.3vw] leading-tight"
                                >
                                  <Markdown>{line}</Markdown>
                                </div>
                              ))}
                          </div>
                        )}
                        {item?.description_3 && (
                          <div className="pt-[1vw]">
                            {item?.description_3
                              ?.split("\n")
                              .map((line, idx) => (
                                <div
                                  key={idx}
                                  className="font-redhatRegular text-[1.3vw] leading-tight"
                                >
                                  <Markdown>{line}</Markdown>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    </Link>
                  </div>
                ) : (
                  <div
                    className="w-full min-h-[80vh] relative bg-gray-200"
                    aria-hidden="true"
                  />
                ))}

              {/* Mobile Banner - only loads on mobile */}
              {isDesktop === false &&
                (item?.mobile_image ? (
                  <div className="w-full h-[180vw] relative">
                    <Link
                      to={
                        item?.link_redirection ? item?.link_redirection : null
                      }
                      target={
                        item?.same_or_different_tab
                          ? item?.same_or_different_tab
                          : undefined
                      }
                      rel={
                        item?.same_or_different_tab === "_blank"
                          ? "noopener noreferrer"
                          : undefined
                      }
                    >
                      {index === 0 ? (
                        <img
                          src={item?.mobile_image?.url}
                          alt={
                            item?.mobile_image?.alternativeText ||
                            "Banner image mobile"
                          }
                          className="object-cover w-full h-full md:min-h-[50vw]"
                          loading="eager"
                          fetchPriority="high"
                        />
                      ) : (
                        <img
                          src={item?.mobile_image?.url}
                          alt={
                            item?.mobile_image?.alternativeText ||
                            "Banner image mobile"
                          }
                          className="object-cover w-full h-full md:min-h-[50vw]"
                          loading="lazy"
                          fetchPriority="high"
                        />
                      )}
                      <div className="w-full absolute top-[43%] text-white">
                        <div className="w-[85%] mx-auto drop-shadow-lg">
                          {item?.banner_text_1 && (
                            <p className="font-redhatMedium text-[5vw]">
                              {item?.banner_text_1}
                            </p>
                          )}
                          {item?.heading && (
                            <p className="font-redhatBold text-[5vw]">
                              {item?.heading}
                            </p>
                          )}
                          {item?.description_1 && (
                            <div className="py-[0.5vw]">
                              {item?.description_1
                                ?.split("\n")
                                .map((line, idx) => (
                                  <div
                                    key={idx}
                                    className="font-redhatRegular text-[3.5vw]"
                                  >
                                    <Markdown>{line}</Markdown>
                                  </div>
                                ))}
                            </div>
                          )}
                          {item?.description_2 && (
                            <div className="pt-[1vw]">
                              {item?.description_2
                                ?.split("\n")
                                .map((line, idx) => (
                                  <div
                                    key={idx}
                                    className="font-redhatRegular text-[3.5vw]"
                                  >
                                    <Markdown>{line}</Markdown>
                                  </div>
                                ))}
                            </div>
                          )}
                          {item?.description_3 && (
                            <div className="pt-[1vw]">
                              {item?.description_3
                                ?.split("\n")
                                .map((line, idx) => (
                                  <div
                                    key={idx}
                                    className="font-redhatRegular text-[3.5vw]"
                                  >
                                    <Markdown>{line}</Markdown>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>
                  </div>
                ) : (
                  <div
                    className="w-full min-h-[80vh] relative bg-gray-200"
                    aria-hidden="true"
                  />
                ))}
            </div>
          ))}
      </Slider>
    </div>
  );
};

export default DynamicAdvisoryBanner;
